import { useState } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Search, RotateCcw, Printer, ScanBarcode } from "lucide-react";
import ReceiptPreview from "../components/ReceiptPreview";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

export default function AcceptReturn() {
  const { getAuthHeader } = useAuth();
  const [searchInput, setSearchInput] = useState("");
  const [searchType, setSearchType] = useState("receipt"); // receipt or barcode
  const [originalSale, setOriginalSale] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [completedReturn, setCompletedReturn] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  const searchSale = async () => {
    if (!searchInput.trim()) {
      toast.error("Please enter a receipt number or barcode");
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`${API}/sales/today`, getAuthHeader());
      let sale = null;
      
      // Search by receipt number
      sale = response.data.find(s => 
        s.receipt_number.toLowerCase().includes(searchInput.toLowerCase()) ||
        s.receipt_number.toLowerCase() === searchInput.toLowerCase()
      );
      
      // If not found by receipt, search by item barcode in sales
      if (!sale) {
        sale = response.data.find(s => 
          s.items.some(item => item.barcode === searchInput)
        );
      }
      
      if (sale) {
        if (sale.is_return) {
          toast.error("This is already a return transaction");
          setOriginalSale(null);
          return;
        }
        setOriginalSale(sale);
        setReturnItems(sale.items.map(item => ({
          ...item,
          return_quantity: 0,
          selected: false
        })));
        toast.success("Sale found! Select items to return.");
      } else {
        toast.error("Sale not found. Try searching by receipt number.");
        setOriginalSale(null);
      }
    } catch (error) {
      toast.error("Failed to search for sale");
    } finally {
      setSearching(false);
    }
  };

  const handleBarcodeKeyPress = (e) => {
    if (e.key === "Enter") {
      searchSale();
    }
  };

  const toggleItemReturn = (index) => {
    setReturnItems(prev => prev.map((item, i) => 
      i === index 
        ? { ...item, selected: !item.selected, return_quantity: !item.selected ? item.quantity : 0 }
        : item
    ));
  };

  const updateReturnQuantity = (index, qty) => {
    const maxQty = returnItems[index].quantity;
    const newQty = Math.min(Math.max(0, qty), maxQty);
    setReturnItems(prev => prev.map((item, i) => 
      i === index ? { ...item, return_quantity: newQty, selected: newQty > 0 } : item
    ));
  };

  const selectAllItems = () => {
    setReturnItems(prev => prev.map(item => ({
      ...item,
      selected: true,
      return_quantity: item.quantity
    })));
  };

  const calculateReturnTotal = () => {
    return returnItems
      .filter(item => item.selected && item.return_quantity > 0)
      .reduce((sum, item) => sum + (item.price * item.return_quantity), 0);
  };

  const handleProcessReturn = async () => {
    const itemsToReturn = returnItems
      .filter(item => item.selected && item.return_quantity > 0)
      .map(item => ({
        product_id: item.product_id,
        name: item.name,
        barcode: item.barcode,
        price: item.price,
        quantity: item.return_quantity,
        discount_percent: item.discount_percent || 0
      }));

    if (itemsToReturn.length === 0) {
      toast.error("Please select items to return");
      return;
    }

    setLoading(true);
    try {
      const returnData = {
        original_sale_id: originalSale.id,
        items: itemsToReturn,
        reason: reason
      };

      const response = await axios.post(`${API}/returns`, returnData, getAuthHeader());
      setCompletedReturn(response.data);
      setShowReceiptModal(true);
      toast.success(`Refund processed: $${calculateReturnTotal().toFixed(2)}`);
      
      // Reset form
      setSearchInput("");
      setOriginalSale(null);
      setReturnItems([]);
      setReason("");
    } catch (error) {
      toast.error("Failed to process return");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fade-in" data-testid="accept-return-page">
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <RotateCcw className="w-6 h-6" />
            Accept Return / Refund
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Search Section */}
          <div className="bg-slate-50 p-4 rounded-lg mb-6">
            <Label className="text-lg font-bold mb-3 block">Scan Barcode or Enter Receipt Number</Label>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <ScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleBarcodeKeyPress}
                  placeholder="Scan barcode or enter receipt # (e.g., NIM-20260302-0001)"
                  className="pl-10 h-14 text-lg font-mono"
                  autoFocus
                  data-testid="return-search-input"
                />
              </div>
              <Button 
                onClick={searchSale} 
                disabled={searching} 
                className="h-14 px-8 bg-blue-600 hover:bg-blue-700 text-lg font-bold"
                data-testid="search-sale-btn"
              >
                {searching ? <span className="loading-spinner" /> : <Search className="w-5 h-5 mr-2" />}
                SEARCH
              </Button>
            </div>
            <p className="text-sm text-slate-500 mt-2">
              Scan the barcode on the receipt or enter the receipt number to find the sale
            </p>
          </div>

          {/* Original Sale Details */}
          {originalSale && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                <h3 className="font-bold text-lg mb-3 text-green-800">✓ Original Sale Found</h3>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500 block">Receipt #</span>
                    <span className="font-mono font-bold text-lg">{originalSale.receipt_number}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Date</span>
                    <span className="font-bold">{new Date(originalSale.created_at).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Payment</span>
                    <span className="font-bold">{originalSale.payment_method}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Original Total</span>
                    <span className="font-bold text-xl text-blue-600">${originalSale.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Select All Button */}
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  onClick={selectAllItems}
                  className="font-bold"
                >
                  Select All Items
                </Button>
              </div>

              {/* Items Table */}
              <div className="overflow-hidden rounded-lg border-2 border-slate-200">
                <table className="cart-table">
                  <thead className="bg-slate-800 text-white">
                    <tr>
                      <th className="text-white">SELECT</th>
                      <th className="text-white">BARCODE</th>
                      <th className="text-white">ITEM NAME</th>
                      <th className="text-white">ORIG QTY</th>
                      <th className="text-white">PRICE</th>
                      <th className="text-white">RETURN QTY</th>
                      <th className="text-white">REFUND</th>
                    </tr>
                  </thead>
                  <tbody>
                    {returnItems.map((item, index) => (
                      <tr key={index} className={item.selected ? "bg-green-50" : ""}>
                        <td>
                          <input
                            type="checkbox"
                            checked={item.selected}
                            onChange={() => toggleItemReturn(index)}
                            className="w-6 h-6 cursor-pointer accent-green-600"
                            data-testid={`return-select-${index}`}
                          />
                        </td>
                        <td className="font-mono font-bold">{item.barcode}</td>
                        <td className="font-bold">{item.name}</td>
                        <td className="font-mono text-center">{item.quantity}</td>
                        <td className="font-mono">${item.price.toFixed(2)}</td>
                        <td>
                          <Input
                            type="number"
                            min="0"
                            max={item.quantity}
                            value={item.return_quantity}
                            onChange={(e) => updateReturnQuantity(index, parseInt(e.target.value) || 0)}
                            className="w-20 h-10 text-center font-bold text-lg"
                            disabled={!item.selected}
                            data-testid={`return-qty-${index}`}
                          />
                        </td>
                        <td className="font-mono font-bold text-green-600 text-lg">
                          ${(item.price * item.return_quantity).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Reason */}
              <div className="space-y-2">
                <Label className="font-bold">Return Reason (Optional)</Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason for return..."
                  rows={2}
                  className="text-lg"
                  data-testid="return-reason-input"
                />
              </div>

              {/* Total and Actions */}
              <div className="flex items-center justify-between p-6 bg-green-100 rounded-lg border-2 border-green-300">
                <div>
                  <span className="text-lg font-bold text-slate-700">TOTAL REFUND:</span>
                  <span className="text-4xl font-black text-green-700 ml-4">${calculateReturnTotal().toFixed(2)}</span>
                </div>
                <Button
                  onClick={handleProcessReturn}
                  disabled={loading || calculateReturnTotal() === 0}
                  className="bg-green-600 hover:bg-green-700 h-14 px-8 text-lg font-bold"
                  data-testid="process-return-btn"
                >
                  {loading ? <span className="loading-spinner" /> : <RotateCcw className="w-5 h-5 mr-2" />}
                  PROCESS REFUND
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Return Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Refund Receipt</DialogTitle>
          </DialogHeader>
          {completedReturn && <ReceiptPreview sale={completedReturn} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
